import React, { useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const STATUS_OPTIONS = ['all', 'open', 'resolved'] as const;
const SEVERITY_OPTIONS = ['all', 'critical', 'medium', 'minor'] as const;
const SEVERITY_LABELS: Record<string, string> = { all: 'ALL', critical: 'CRITICAL', medium: 'ANNOYING', minor: 'MINOR' };

export interface BugReport {
    id: string;
    status: 'open' | 'resolved';
    severity: 'critical' | 'medium' | 'minor';
    module: string;
    user_name?: string;
    user_email?: string;
    description: string;
    screenshot?: string | null;
    created_at: string;
}

export interface BugsTableProps {
    bugReports: BugReport[];
    bugsLoading?: boolean;
    onRefresh?: () => void;
    onResolve?: (id: string) => void;
    onDelete?: (id: string) => void;
}

const statusBadgeConfig = {
    open: { className: 'bg-red-500/15 text-red-400 border-red-500/30', icon: 'bx bxs-error-circle', label: 'OPEN' },
    resolved: { className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: 'bx bx-check-circle', label: 'DONE' },
} as const;

const severityBadgeConfig = {
    critical: { className: 'bg-red-500/15 text-red-400 border-red-500/30', icon: 'bx bxs-error-circle', label: 'CRITICAL' },
    medium: { className: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: 'bx bxs-error', label: 'ANNOYING' },
    minor: { className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: 'bx bxs-info-circle', label: 'MINOR' },
} as const;

export const BugsTable: React.FC<BugsTableProps> = ({
    bugReports,
    bugsLoading = false,
    onRefresh,
    onResolve,
    onDelete,
}) => {
    const [bugFilter, setBugFilter] = useState<{ status: string; severity: string; module: string }>({ status: 'all', severity: 'all', module: 'all' });
    const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);

    const filtered = bugReports.filter((b) => {
        if (bugFilter.status !== 'all' && b.status !== bugFilter.status) return false;
        if (bugFilter.severity !== 'all' && b.severity !== bugFilter.severity) return false;
        if (bugFilter.module !== 'all' && b.module !== bugFilter.module) return false;
        return true;
    });

    const modules = [...new Set(bugReports.map((b) => b.module).filter(Boolean))];

    return (
        <div data-testid="admin-view-bugs" className="flex flex-col gap-4">
            {/* Header Bar */}
            <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                    <Badge variant="destructive" size="xs">
                        {bugReports.filter(b => b.status === 'open').length} OPEN
                    </Badge>
                    <Badge size="xs" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                        {bugReports.filter(b => b.status === 'resolved').length} RESOLVED
                    </Badge>
                </div>
                <button
                    data-testid="admin-bugs-refresh-btn"
                    onClick={onRefresh}
                    className="bg-lime-400/10 border border-lime-400 text-lime-400 px-4 py-2 rounded-lg cursor-pointer text-xs font-extrabold flex items-center gap-1.5 hover:bg-lime-400/20 transition-colors"
                >
                    <i className={`bx bx-refresh ${bugsLoading ? 'bx-spin' : ''}`}></i> REFRESH
                </button>
            </div>

            {/* Filter Bar */}
            <div data-testid="admin-bugs-filters" className="flex gap-3 flex-wrap items-center">
                <div className="flex items-center gap-1.5">
                    <span className="text-[0.55rem] font-extrabold text-muted-foreground mr-1">STATUS</span>
                    <ToggleGroup
                        variant="outline"
                        size="sm"
                        value={[bugFilter.status]}
                        onValueChange={(values) => {
                            if (values.length > 0) {
                                setBugFilter(f => ({ ...f, status: values[values.length - 1] }));
                            }
                        }}
                    >
                        {STATUS_OPTIONS.map(v => (
                            <ToggleGroupItem
                                key={v}
                                value={v}
                                data-testid={`admin-bugs-filter-status-${v}`}
                                className="text-[0.6rem] font-bold uppercase px-2.5 h-6 data-[pressed]:bg-lime-400/15 data-[pressed]:text-lime-400 data-[pressed]:border-lime-400"
                            >
                                {v}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-[0.55rem] font-extrabold text-muted-foreground mr-1">SEVERITY</span>
                    <ToggleGroup
                        variant="outline"
                        size="sm"
                        value={[bugFilter.severity]}
                        onValueChange={(values) => {
                            if (values.length > 0) {
                                setBugFilter(f => ({ ...f, severity: values[values.length - 1] }));
                            }
                        }}
                    >
                        {SEVERITY_OPTIONS.map(v => (
                            <ToggleGroupItem
                                key={v}
                                value={v}
                                data-testid={`admin-bugs-filter-severity-${v}`}
                                className="text-[0.6rem] font-bold uppercase px-2.5 h-6 data-[pressed]:bg-lime-400/15 data-[pressed]:text-lime-400 data-[pressed]:border-lime-400"
                            >
                                {SEVERITY_LABELS[v] || v.toUpperCase()}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>
                {modules.length > 1 && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-[0.55rem] font-extrabold text-muted-foreground mr-1">MODULE</span>
                        <ToggleGroup
                            variant="outline"
                            size="sm"
                            value={[bugFilter.module]}
                            onValueChange={(values) => {
                                if (values.length > 0) {
                                    setBugFilter(f => ({ ...f, module: values[values.length - 1] }));
                                }
                            }}
                        >
                            {['all', ...modules].map(v => (
                                <ToggleGroupItem
                                    key={v}
                                    value={v}
                                    className="text-[0.6rem] font-bold uppercase px-2.5 h-6 data-[pressed]:bg-lime-400/15 data-[pressed]:text-lime-400 data-[pressed]:border-lime-400"
                                >
                                    {v}
                                </ToggleGroupItem>
                            ))}
                        </ToggleGroup>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="rounded-xl border border-border overflow-hidden">
                <Table data-testid="admin-bugs-table" className="text-xs">
                    <TableHeader>
                        <TableRow data-testid="admin-bugs-table-header">
                            {['Status', 'Severity', 'Module', 'User', 'Description', 'Screenshot', 'Date', 'Actions'].map(h => (
                                <TableHead key={h} className="text-[0.6rem] font-extrabold tracking-wide text-muted-foreground uppercase">
                                    {h}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} className="py-8 text-center text-muted-foreground italic">
                                    No bug reports
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map((bug) => {
                                const statusCfg = statusBadgeConfig[bug.status];
                                const severityCfg = severityBadgeConfig[bug.severity];
                                return (
                                    <TableRow
                                        key={bug.id}
                                        data-testid={`admin-bugs-row-${bug.id}`}
                                        className={bug.status === 'resolved' ? 'opacity-50' : ''}
                                    >
                                        <TableCell data-testid={`admin-bugs-row-${bug.id}-status`}>
                                            <Badge variant="outline" size="xs" className={statusCfg.className}>
                                                <i className={statusCfg.icon} style={{ fontSize: '0.7rem' }}></i> {statusCfg.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell data-testid={`admin-bugs-row-${bug.id}-severity`}>
                                            <Badge variant="outline" size="xs" className={severityCfg.className}>
                                                <i className={severityCfg.icon} style={{ fontSize: '0.7rem' }}></i> {severityCfg.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell data-testid={`admin-bugs-row-${bug.id}-module`}>
                                            <Badge variant="outline" size="xs" className="bg-lime-400/10 text-lime-400 border-lime-400/30">
                                                {(bug.module || '').toUpperCase()}
                                            </Badge>
                                        </TableCell>
                                        <TableCell data-testid={`admin-bugs-row-${bug.id}-user`} className="text-foreground font-bold">
                                            {bug.user_name || (bug.user_email || '').split('@')[0] || '—'}
                                        </TableCell>
                                        <TableCell
                                            data-testid={`admin-bugs-row-${bug.id}-description`}
                                            className="max-w-[300px] truncate text-muted-foreground"
                                            title={bug.description}
                                        >
                                            {bug.description}
                                        </TableCell>
                                        <TableCell data-testid={`admin-bugs-row-${bug.id}-screenshot`}>
                                            {bug.screenshot ? (
                                                <img
                                                    src={bug.screenshot}
                                                    alt="Bug screenshot"
                                                    onClick={() => setScreenshotPreview(bug.screenshot!)}
                                                    className="w-[60px] h-[36px] object-cover rounded cursor-pointer border border-white/10 transition-all hover:border-lime-400 hover:scale-110"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground/40 text-sm"><i className='bx bx-camera-off'></i></span>
                                            )}
                                        </TableCell>
                                        <TableCell data-testid={`admin-bugs-row-${bug.id}-date`} className="text-muted-foreground font-mono text-[0.7rem]">
                                            {new Date(bug.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}{' '}
                                            {new Date(bug.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                        </TableCell>
                                        <TableCell data-testid={`admin-bugs-row-${bug.id}-actions`}>
                                            <div className="flex gap-1.5 items-center">
                                                {bug.status === 'open' && (
                                                    <button
                                                        data-testid={`admin-bugs-row-${bug.id}-resolve-btn`}
                                                        onClick={() => onResolve?.(bug.id)}
                                                        className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-2.5 py-1 rounded-md cursor-pointer text-[0.6rem] font-extrabold hover:bg-emerald-500/20 transition-colors"
                                                    >
                                                        ✓ RESOLVE
                                                    </button>
                                                )}
                                                <button
                                                    data-testid={`admin-bugs-row-${bug.id}-delete-btn`}
                                                    onClick={() => onDelete?.(bug.id)}
                                                    className="bg-red-500/10 border border-red-500/20 text-red-400 px-2 py-1 rounded-md cursor-pointer text-sm hover:bg-red-500/20 transition-colors"
                                                >
                                                    <i className='bx bx-trash'></i>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Screenshot Preview Modal */}
            {screenshotPreview && (
                <div
                    onClick={() => setScreenshotPreview(null)}
                    className="fixed inset-0 bg-black/85 backdrop-blur-sm flex justify-center items-center z-[2000] cursor-pointer"
                >
                    <img
                        src={screenshotPreview}
                        alt="Screenshot preview"
                        className="max-w-[90vw] max-h-[90vh] rounded-xl border-2 border-lime-400 shadow-[0_0_60px_rgba(163,230,53,0.2)]"
                    />
                </div>
            )}
        </div>
    );
};
